'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { toAnalyticsReasonCode, trackWalletAction } from '@workspace/analytics/posthog/events';
import { Button } from '@workspace/ui/components/button';
import { Badge } from '@workspace/ui/components/badge';
import { CoinStack2Icon } from '@workspace/ui/components/custom-icons/CoinStack2Icon';
import { cn, PERMISSIONS, trackEvent } from '@workspace/ui/lib';
import {
  CircleAlert,
  CircleCheck,
  Clock,
  CreditCard,
  Globe,
  Info,
  ShieldCheck,
  Truck,
  TriangleAlert,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { type ReactNode, useEffect, useState } from 'react';
import { FeatureLockedDialog } from '@/components/feature-lock/feature-locked-dialog';
import { RestrictionDialog } from '@/components/restrictions/restriction-dialog';
import { CardActivationConfirmDialog } from '@/components/wallet/card-activation-confirm-dialog';
import { CardOrderSuccessDialog } from '@/components/wallet/card-order-success-dialog';
import { KYCVerificationDialog } from '@/components/wallet/kyc-verification-dialog';
import { ReviewCardInfoDialog } from '@/components/wallet/review-card-info-dialog';
import { CardMaintenanceAlert, CardMaintenanceTooltip } from '@/components/wallet/card-maintenance';
import { useActivateCard } from '@/hooks/use-cards';
import { IS_EPS_CARD_MAINTENANCE_ACTIVE } from '@/lib/card-maintenance';
import { useCountries } from '@/hooks/use-countries';
import { useFeatureLockGate } from '@/hooks/use-feature-lock';
import {
  hasEpsCardPermission,
  useCurrentKYCVerification,
  useKYCVerifications,
} from '@/hooks/use-kyc';
import { useRestrictionGate } from '@/hooks/use-user-restriction';
import { mapKYCDataToFormData } from '@/lib/kyc-data-mapper';

export type CardRequestStatus =
  | 'start-verification'
  | 'continue-verification'
  | 'ready-to-order'
  | 'verification-progress'
  | 'rejected'
  | 'final-rejected'
  | 'card-declined'
  | 'preparing-delivery'
  | 'on-way'
  | 'inactive'
  | null;

export interface CardStatusData {
  id: number;
  eps_card_id: string;
  service?: string;
  tracking_number?: string | null;
  tracking_link?: string | null;
}

interface CardRequestStatusProps {
  status: CardRequestStatus;
  cardData?: CardStatusData;
  // Called after a successful activation. The success dialog is owned by the parent
  // so it survives the transition from this banner to the active card view.
  onActivationSuccess?: () => void;
}

type BadgeVariant =
  | 'filled-light-pink'
  | 'filled-dark-orange'
  | 'filled-light-red'
  | 'filled-grey-2'
  | 'filled-light-blue-2'
  | 'filled-light-purple'
  | 'filled-light-green'
  | 'filled-grey';

interface WidgetContent {
  badge: string;
  badgeVariant: BadgeVariant;
  badgeClassName?: string;
  badgeIcon: ReactNode;
  title: string;
  description?: string;
  info?: string;
  infoIcon?: 'check' | 'info';
  buttonText?: string;
  buttonAction?: () => void;
  showFeatures: boolean;
  cardImage: 'widget' | 'active';
}

interface CardFeature {
  icon: ReactNode;
  text: string;
  alignWithFirstLine?: boolean;
}

const CARD_FEATURES: CardFeature[] = [
  {
    icon: <CreditCard className="h-5 w-5 text-pink-400" />,
    text: 'Track and manage your spending in one place',
    alignWithFirstLine: true,
  },
  {
    icon: <CoinStack2Icon className="h-5 w-5 text-pink-400" />,
    text: 'Free to issue, no monthly fee',
  },
  {
    icon: <Globe className="h-5 w-5 text-pink-400" />,
    text: 'Spend in-store, online or abroad',
  },
];

function CardFeatureRow({ icon, text, alignWithFirstLine }: CardFeature) {
  return (
    <div className={cn('flex gap-1.5', alignWithFirstLine ? 'items-start' : 'items-center')}>
      <div className="flex shrink-0 items-center py-1">{icon}</div>
      <p className="text-neutral-200 dark:text-neutral-100 text-xs leading-5">{text}</p>
    </div>
  );
}

const CARDS_CDN = 'https://i.nativeteams.com/assets/app/wallet/cards';
const WIDGET_CARD_IMAGE = `${CARDS_CDN}/card-widget-photo.webp`;
const SHIPPING_BACKGROUND_IMAGE = `${CARDS_CDN}/card-shipping-background.webp`;
const ACTIVE_CARD_IMAGE = `${CARDS_CDN}/card-active-photo.webp`;

const BUTTON_LOADING_TEXT: Partial<Record<NonNullable<CardRequestStatus>, string>> = {
  'on-way': 'Activating card',
  'start-verification': 'Starting verification',
  'continue-verification': 'Starting verification',
  rejected: 'Resubmitting',
};

interface CardStatusPhotoProps {
  kind: WidgetContent['cardImage'];
}

function CardStatusPhoto({ kind }: CardStatusPhotoProps) {
  if (kind === 'widget') {
    return (
      <Image
        src={WIDGET_CARD_IMAGE}
        alt="Native Teams card"
        width={540}
        height={360}
        className="h-auto w-[270px] max-w-full shrink-0 rounded-3xl"
        priority
      />
    );
  }

  return (
    <Image
      src={ACTIVE_CARD_IMAGE}
      alt="Native Teams card"
      width={1024}
      height={526}
      className="h-auto max-h-[263px] w-full object-contain"
      priority
    />
  );
}

interface CardShipmentWidgetProps {
  content: WidgetContent;
  trackingLink?: string;
  isButtonLoading: boolean;
  buttonLoadingText: string;
  isActivateDisabledByMaintenance: boolean;
  showMaintenanceAlert: boolean;
}

function CardShipmentWidget({
  content,
  trackingLink,
  isButtonLoading,
  buttonLoadingText,
  isActivateDisabledByMaintenance,
  showMaintenanceAlert,
}: CardShipmentWidgetProps) {
  return (
    // On mobile the photo is 170% of the box width and shifted left, so the card is whole and centred
    // and only cut where the photo itself ends at the bottom. The bottom padding reserves the part of
    // the photo that shows the card (85% of the box width) plus a 25px gap, so the card stays below
    // the text and buttons however many buttons there are. grey-100 continues the photo's top edge.
    // The photo is light in both themes, so the text and buttons use the theme-independent black styles.
    <div className="relative isolate flex w-full flex-col items-start gap-8 overflow-hidden rounded-[32px] bg-grey-100 p-6 pb-[calc(85%+25px)] shadow-[0px_17px_38px_0px_rgba(42,43,65,0.05)] sm:min-h-[542px] sm:p-8">
      <div className="absolute bottom-0 -left-[67%] -z-10 aspect-[1800/1626] w-[170%] sm:inset-0 sm:aspect-auto sm:w-auto">
        <Image
          src={SHIPPING_BACKGROUND_IMAGE}
          alt=""
          fill
          sizes="(max-width: 640px) 170vw, 720px"
          className="object-cover object-right-bottom"
          priority
        />
      </div>
      <div className="flex max-w-[405px] flex-col items-start gap-5">
        <Badge variant={content.badgeVariant} size="lg" icon={content.badgeIcon}>
          {content.badge}
        </Badge>
        <div className="flex flex-col gap-3">
          <p className="text-primary text-[28px] leading-9 font-bold">{content.title}</p>
          <p className="text-primary text-sm leading-5">{content.description}</p>
        </div>
        {showMaintenanceAlert ? <CardMaintenanceAlert className="max-w-[405px]" titleOnly /> : null}
      </div>
      {content.buttonText || trackingLink ? (
        <div className="flex w-full flex-col gap-4 sm:w-fit">
          {content.buttonText ? (
            isActivateDisabledByMaintenance ? (
              <CardMaintenanceTooltip>
                <Button
                  variant="black-primary"
                  size="sm"
                  onClick={content.buttonAction}
                  disabled={isActivateDisabledByMaintenance}
                >
                  {content.buttonText}
                </Button>
              </CardMaintenanceTooltip>
            ) : (
              <Button
                variant="black-primary"
                size="sm"
                onClick={content.buttonAction}
                isLoading={isButtonLoading}
                loadingText={buttonLoadingText}
              >
                {content.buttonText}
              </Button>
            )
          ) : null}
          {trackingLink ? (
            <Button variant="black-secondary" size="sm" asChild>
              <a href={trackingLink} target="_blank" rel="noopener noreferrer">
                <Truck />
                Track your delivery
              </a>
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function CardRequestStatus({
  status,
  cardData,
  onActivationSuccess,
}: CardRequestStatusProps) {
  const [showActivationConfirm, setShowActivationConfirm] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [prefillData, setPrefillData] = useState<object | null>(null);
  const [shouldTriggerKYC, setShouldTriggerKYC] = useState(false);
  const [showCardOrderSuccess, setShowCardOrderSuccess] = useState(false);

  const activateCardMutation = useActivateCard();
  const { guard, restrictionDialogProps } = useRestrictionGate();
  const { guard: guardOrderCard, featureLockedDialogProps: orderCardFeatureLockedDialogProps } =
    useFeatureLockGate(PERMISSIONS.CREATE_SELF_CARD_REQUESTS);
  const { data: kycVerifications, isLoading: isKycLoading } = useKYCVerifications();
  const { data: currentKyc } = useCurrentKYCVerification();
  const { data: countries, isLoading: isCountriesLoading } = useCountries();

  const canOrderCard = hasEpsCardPermission(currentKyc);

  useEffect(() => {
    if (status === 'start-verification' || status === 'ready-to-order' || status === null) {
      trackEvent('banner_view', 'card');
    }
  }, [status]);

  const handleContactSupport = () => {
    window.dispatchEvent(new Event('toggle-hubspot-chat'));
  };

  const handleActivateCard = async () => {
    if (!cardData) return;

    try {
      await activateCardMutation.mutateAsync({
        service: cardData.service || 'eps',
        cardId: cardData.id.toString(),
      });
      trackWalletAction('card_activated', { flow: 'card', result: 'success' });
      setShowActivationConfirm(false);
      onActivationSuccess?.();
    } catch (error) {
      trackWalletAction('card_activated', {
        flow: 'card',
        result: 'failure',
        ...(error instanceof Error ? { reason_code: toAnalyticsReasonCode(error) } : {}),
      });
      console.error('Failed to activate card:', error);
    }
  };

  const openReviewWithPrefill = () => {
    const kycData = kycVerifications?.[0];
    if (countries?.length && kycData) {
      setPrefillData(mapKYCDataToFormData(kycData, countries));
    } else {
      setPrefillData({});
    }
    setIsReviewDialogOpen(true);
  };

  const handleResubmit = () => {
    guardOrderCard(() => {
      guard(() => {
        if (!canOrderCard) {
          setShouldTriggerKYC(true);
          return;
        }
        openReviewWithPrefill();
      });
    });
  };

  const handleKYCSuccess = (kycPrefillData: object) => {
    setPrefillData(kycPrefillData);
    setIsReviewDialogOpen(true);
  };

  const handleReviewSuccess = () => {
    setIsReviewDialogOpen(false);
    setShowCardOrderSuccess(true);
  };

  const handleOrderCardClick = () => {
    trackEvent('banner_click', 'card', { action: 'order_card' });

    guardOrderCard(() => {
      guard(() => {
        openReviewWithPrefill();
      });
    });
  };

  const handleStartVerification = () => {
    trackEvent('banner_click', 'card', { action: 'start_verification' });
    guardOrderCard(() => {
      guard(() => {
        setShouldTriggerKYC(true);
      });
    });
  };

  const handleContinueVerification = () => {
    trackEvent('banner_click', 'card', { action: 'continue_verification' });
    guardOrderCard(() => {
      guard(() => {
        setShouldTriggerKYC(true);
      });
    });
  };

  const spendTitle = 'Spend your wallet balance anywhere';

  const contentByStatus: Record<Exclude<CardRequestStatus, null>, WidgetContent> = {
    'start-verification': {
      badge: 'Verification required',
      badgeVariant: 'filled-light-pink',
      badgeIcon: <ShieldCheck />,
      title: spendTitle,
      info: 'Complete verification step to order your physical card',
      infoIcon: 'info',
      buttonText: 'Start verification',
      buttonAction: handleStartVerification,
      showFeatures: true,
      cardImage: 'widget',
    },
    'continue-verification': {
      badge: 'Not ordered',
      badgeVariant: 'filled-grey-2',
      badgeClassName: 'text-grey-700',
      badgeIcon: null,
      title: spendTitle,
      info: 'Identity verification incomplete. Continue to complete your verification.',
      infoIcon: 'info',
      buttonText: 'Continue verification',
      buttonAction: handleContinueVerification,
      showFeatures: true,
      cardImage: 'widget',
    },
    'ready-to-order': {
      badge: 'Not ordered',
      badgeVariant: 'filled-grey-2',
      badgeClassName: 'text-grey-700',
      badgeIcon: <Info />,
      title: spendTitle,
      info: 'Your identity is verified. Your card is ready to order.',
      infoIcon: 'check',
      buttonText: 'Order card',
      buttonAction: handleOrderCardClick,
      showFeatures: true,
      cardImage: 'widget',
    },
    'verification-progress': {
      badge: 'Under review',
      badgeVariant: 'filled-light-blue-2',
      badgeIcon: <Info />,
      title: spendTitle,
      info: "Your documents are being reviewed by our team. This usually takes 1–2 business days. We'll notify you when it's done.",
      infoIcon: 'info',
      showFeatures: true,
      cardImage: 'widget',
    },
    rejected: {
      badge: 'Rejected',
      badgeVariant: 'filled-dark-orange',
      badgeIcon: <TriangleAlert />,
      title: spendTitle,
      info: 'Some of your submitted documents require revision. Please review the feedback and resubmit to continue.',
      infoIcon: 'info',
      buttonText: 'Resubmit',
      buttonAction: handleResubmit,
      showFeatures: true,
      cardImage: 'widget',
    },
    'final-rejected': {
      badge: 'Final rejected',
      badgeVariant: 'filled-light-red',
      badgeClassName: 'text-red-500',
      badgeIcon: <CircleAlert />,
      title: spendTitle,
      info: 'We could not verify your information. If you have questions, contact our support team.',
      infoIcon: 'info',
      buttonText: 'Contact us',
      buttonAction: handleContactSupport,
      showFeatures: true,
      cardImage: 'widget',
    },
    'card-declined': {
      badge: 'Declined',
      badgeVariant: 'filled-light-red',
      badgeIcon: <CircleAlert />,
      title: 'Your card request has been rejected',
      info: 'Your verification was approved, but the card request was rejected. Please try again.',
      buttonText: 'Reorder card',
      buttonAction: handleOrderCardClick,
      showFeatures: true,
      cardImage: 'widget',
    },
    'preparing-delivery': {
      badge: 'Preparing shipment',
      badgeVariant: 'filled-light-purple',
      badgeIcon: <Clock />,
      title: 'Your card is being prepared',
      description:
        "We're getting your card ready for shipment. You'll receive a tracking number by email once it's on the way.",
      showFeatures: false,
      cardImage: 'active',
    },
    'on-way': {
      badge: 'Card shipped',
      badgeVariant: 'filled-light-green',
      badgeIcon: <CircleCheck />,
      title: 'Your card is on its way!',
      description:
        'Your card has been dispatched and is heading to you. Track its journey with the details sent to your email.',
      buttonText: 'Activate card',
      buttonAction: () => setShowActivationConfirm(true),
      showFeatures: false,
      cardImage: 'active',
    },
    inactive: {
      badge: 'Inactive',
      badgeVariant: 'filled-grey',
      badgeIcon: <CircleAlert />,
      title: 'Physical card',
      buttonText: 'See my card',
      showFeatures: false,
      cardImage: 'active',
    },
  };

  const content = status ? contentByStatus[status] : contentByStatus['start-verification'];
  const trackingLink = cardData?.tracking_link?.trim();
  const isShipment = status === 'preparing-delivery' || status === 'on-way';
  const isActivateDisabledByMaintenance = IS_EPS_CARD_MAINTENANCE_ACTIVE && status === 'on-way';
  const isActivating = activateCardMutation.isPending && status === 'on-way';
  // These buttons open the order form, which is prefilled from the KYC data.
  const opensOrderForm =
    status === 'ready-to-order' ||
    status === 'card-declined' ||
    (status === 'rejected' && canOrderCard);
  const isPrefillLoading = opensOrderForm && (isKycLoading || isCountriesLoading);
  const isButtonLoading = isActivating || shouldTriggerKYC || isPrefillLoading;
  const buttonLoadingText = BUTTON_LOADING_TEXT[status ?? 'start-verification'] ?? 'Loading';
  const cardImage = <CardStatusPhoto kind={content.cardImage} />;

  const dialogs = (
    <>
      <CardActivationConfirmDialog
        open={showActivationConfirm}
        onOpenChange={setShowActivationConfirm}
        onConfirm={handleActivateCard}
        isLoading={activateCardMutation.isPending}
      />

      <KYCVerificationDialog
        variant="direct"
        onKYCSuccess={handleKYCSuccess}
        isKYCDialogOpen={shouldTriggerKYC}
        setIsKYCDialogOpen={setShouldTriggerKYC}
      />
      <ReviewCardInfoDialog
        isReviewDialogOpen={isReviewDialogOpen}
        setIsReviewDialogOpen={setIsReviewDialogOpen}
        prefillData={prefillData}
        onSuccess={handleReviewSuccess}
      />
      <CardOrderSuccessDialog open={showCardOrderSuccess} onOpenChange={setShowCardOrderSuccess} />
      <RestrictionDialog {...restrictionDialogProps} />
      <FeatureLockedDialog {...orderCardFeatureLockedDialogProps} />
    </>
  );

  if (isShipment) {
    return (
      <>
        <CardShipmentWidget
          content={content}
          trackingLink={trackingLink}
          isButtonLoading={isButtonLoading}
          buttonLoadingText={buttonLoadingText}
          isActivateDisabledByMaintenance={isActivateDisabledByMaintenance}
          showMaintenanceAlert={Boolean(cardData)}
        />
        {dialogs}
      </>
    );
  }

  return (
    <>
      <div className="bg-grey-25 dark:bg-grey-1000 flex w-full flex-col gap-8 rounded-[32px] p-6 shadow-[0px_17px_38px_0px_rgba(42,43,65,0.05)] sm:p-11">
        <div className="flex w-full flex-col gap-8">
          <div className="flex w-full flex-col gap-3">
            <div className="flex w-full flex-wrap items-center justify-between gap-3">
              <p className="text-grey-800 dark:text-grey-400 text-sm leading-6 font-medium">
                Native Teams physical card
              </p>
              <Badge
                variant={content.badgeVariant}
                size="lg"
                icon={content.badgeIcon}
                className={content.badgeClassName}
              >
                {content.badge}
              </Badge>
            </div>
            <p className="text-neutral-200 dark:text-neutral-100 text-xl leading-6 font-medium">
              {content.title}
            </p>
          </div>

          {cardData ? <CardMaintenanceAlert titleOnly /> : null}

          {content.showFeatures ? (
            <div className="flex w-full flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-3">
              {cardImage}
              <div className="flex flex-1 flex-col gap-4">
                {CARD_FEATURES.map((feature) => (
                  <CardFeatureRow key={feature.text} {...feature} />
                ))}
              </div>
            </div>
          ) : (
            cardImage
          )}

          {content.info ? (
            <div className="bg-grey-50 dark:bg-grey-900 flex w-full items-start gap-2 rounded-[20px] px-4 py-5">
              {content.infoIcon === 'info' ? (
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
              ) : (
                <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-500 dark:text-green-400" />
              )}
              <p className="text-grey-900 dark:text-grey-300 text-sm leading-5 font-medium">
                {content.info}
              </p>
            </div>
          ) : null}
        </div>

        {content.buttonText ? (
          status === 'inactive' && cardData?.id ? (
            <Button variant="primary" size="base" className="w-full sm:w-[200px]" asChild>
              <Link href={`/card/${cardData.id}`}>See my card</Link>
            </Button>
          ) : (
            <Button
              variant="primary"
              size="base"
              className="w-full sm:w-[200px]"
              onClick={content.buttonAction}
              isLoading={isButtonLoading}
              loadingText={buttonLoadingText}
            >
              {content.buttonText}
            </Button>
          )
        ) : null}
      </div>

      {dialogs}
    </>
  );
}
